import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '~/components/ui';
import { useGetCannedTallyLengthsForCategory } from '~/api/canned-tally';
import type { Quantity } from '~/graphql/graphql';
import { isValidBundleCountText } from '~/components/common/tallyBundleCounts';

type RandomTallyBundlesGridProps = {
  platformProductId: string;
  bundleCountsByLengthId: Record<string, string>;
  onBundleCountChange: (lengthId: string, value: string) => void;
  piecesPerBundle: Pick<Quantity, 'value' | 'unit'>;
  extraColumnHeaders?: ReactNode;
  renderExtraColumns?: (lengthId: string) => ReactNode;
  disabled?: boolean;
  testId?: string;
};

const NO_EXTRA_COLUMNS = () => null;

export function RandomTallyBundlesGrid({
  platformProductId,
  bundleCountsByLengthId,
  onBundleCountChange,
  piecesPerBundle,
  extraColumnHeaders,
  renderExtraColumns = NO_EXTRA_COLUMNS,
  disabled = false,
  testId,
}: Readonly<RandomTallyBundlesGridProps>) {
  const { t } = useTranslation(['order-builder', 'order']);
  const { data: candidateLengthsData, isLoading: isLoadingLengths } =
    useGetCannedTallyLengthsForCategory(platformProductId);
  const candidateLengths =
    candidateLengthsData?.cannedTallyLengthsForCategory ?? [];

  if (isLoadingLengths) {
    return (
      <div
        data-testid={testId}
        className="p-4 text-center text-neut-sec text-sm"
      >
        {t('order_builder.create_random_tally.loading_lengths')}
      </div>
    );
  }

  if (candidateLengths.length === 0) {
    return (
      <div
        data-testid={testId}
        className="p-4 text-center text-neut-sec text-sm"
      >
        {t('order_builder.create_random_tally.no_lengths')}
      </div>
    );
  }

  return (
    <table data-testid={testId} className="w-full table-fixed text-sm">
      <thead>
        <tr className="text-left text-neut-sec">
          <th className="font-normal pb-2 w-20 whitespace-nowrap">
            {t('order_builder.create_random_tally.length_column')}
          </th>
          <th className="font-normal pb-2 w-28">
            {t('order_builder.create_random_tally.quantity_column')}
            <br />
            <span className="font-normal normal-case text-neut-400">
              {piecesPerBundle.value} {piecesPerBundle.unit}/
              {t('order.bundles_one', { ns: 'order' })}
            </span>
          </th>
          {extraColumnHeaders}
        </tr>
      </thead>
      <tbody>
        {candidateLengths.map(({ length }) => {
          const bundleCountText = bundleCountsByLengthId[length.id] ?? '';
          return (
            <tr key={length.id} className="border-t border-neut-border">
              <td className="py-2 align-top text-neut-prim">
                {length.displayValue}
              </td>
              <td className="py-2">
                <Input
                  name={length.id}
                  type="number"
                  min={0}
                  step="1"
                  containerClassName="min-w-0"
                  disabled={disabled}
                  aria-label={t(
                    'order_builder.create_random_tally.bundle_count_for_length',
                    { length: length.displayValue }
                  )}
                  value={bundleCountText}
                  error={
                    isValidBundleCountText(bundleCountText)
                      ? undefined
                      : t(
                          'order_builder.create_random_tally.whole_bundles_only'
                        )
                  }
                  onChange={(_, value) => onBundleCountChange(length.id, value)}
                />
              </td>
              {renderExtraColumns(length.id)}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
